import React, { useEffect, useState } from "react";
import { Title } from "../../../../../stories/Title/Title";
import Close from "../../../../../images/Close.svg";
import { Button } from "../../../../../stories/Button/Button";
import useFormJson from "../../../../../json/useFormJson";
import { Label } from "../../../../../stories/Label/Label";
import Input from "../../../../../stories/Input/Input";
import TextArea from "../../../../../stories/TextArea/TextArea";
import {
  AddProjectPmuRemark,
  UpdateProjectPmuRemark,
} from "../../masters/projects/queries/mutations";
import {
  AddProjectPmuRemarkResult,
  AddProjectPmuRemarkVar,
  AtsProjectDailyReportsByIdResult,
  AtsProjectDailyReportsOrderField,
  UpdateProjectPmuRemarkResult,
  UpdateProjectPmuRemarkVar,
} from "../../masters/projects/queries/types";
import { useLazyQuery, useMutation } from "@apollo/client";
import { emptyMessage } from "../../../../../utils/constants";
import { msgType } from "../../../../../utils/types";
import { Operation } from "../../../../../utils/Enum.types";
import useUrlParams from "../../../../../customHooks/useUrlParams";
import {
  toInputStandardDate,
  toIsoDate,
} from "../../../../../utils/UtilFunctions";
import useServerDateandTime from "../../../../../customHooks/useServerDateandTime";
import useLoggedInUserDetails from "../../../../../customHooks/useLoggedInUserDetails";
import {
  AtsProjectDailyReportsById,
  GetProjectDailyReports,
} from "../../masters/projects/queries/queries";
import { OrderDirection, nodeVar } from "../../../../../utils/commonQueryTypes";
import MessageModal from "../../../../../customComponents/MessageModal";
import LoadingModal from "../../../../pages/LoadingModal";
import { TextField } from "@mui/material";

interface Props {
  setDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  operation: Operation;
  AtsProjectDailyReportsID: number;
}

const FillRemarks = ({
  setDrawer,
  operation,
  AtsProjectDailyReportsID,
}: Props) => {
  const { FillWorkProgress } = useFormJson();

  const [pmuRemarks, setPmuRemarks] = useState("");

  const { customer_id, projectID } = useUrlParams();
  const { serverDate } = useServerDateandTime();
  const { user_details } = useLoggedInUserDetails();
  const [message, setMessage] = useState<msgType>(emptyMessage);
  const [date, setDate] = useState<string>(serverDate);

  const [AddPmuRemarks, { loading: addingRemarks }] = useMutation<
    AddProjectPmuRemarkResult,
    AddProjectPmuRemarkVar
  >(AddProjectPmuRemark, {
    onError: (error) => {
      setMessage({
        flag: true,
        message: error.message,
        operation: Operation.NONE,
      });
    },
  });

  const [atsProjectDailyReportsById] = useLazyQuery<
    AtsProjectDailyReportsByIdResult,
    nodeVar
  >(AtsProjectDailyReportsById);

  const [updateProjectPmuRemark, { loading: updatingRemarks }] = useMutation<
    UpdateProjectPmuRemarkResult,
    UpdateProjectPmuRemarkVar
  >(UpdateProjectPmuRemark, {
    onError: (error) => {
      setMessage({
        flag: true,
        message: error.message,
        operation: Operation.NONE,
      });
    },
  });

  const handleAddRemarks = () => {
    if (customer_id && projectID) {
      if (operation === Operation.CREATE) {
        AddPmuRemarks({
          variables: {
            pmuRemarks,
            reportDate: toIsoDate(serverDate),
            projectID,
            customerID: customer_id,
            user_details,
          },
          refetchQueries: [
            {
              query: GetProjectDailyReports,
              variables: {
                after: null,
                customerID: customer_id,
                projectID,
                first: 12,
                orderBy: {
                  direction: OrderDirection.ASC,
                  field: AtsProjectDailyReportsOrderField.REPORT_DATE,
                },
              },
            },
          ],
        }).then(({ data }) => {
          if (data && data.AddProjectPmuRemark) {
            setMessage({
              flag: true,
              message: "Remarks added successfully",
              operation: Operation.CREATE,
            });
          }
        });
      } else {
        updateProjectPmuRemark({
          variables: {
            pmuRemarks,
            AtsProjectDailyReportsID,
            customerID: customer_id,
            user_details,
          },
          refetchQueries: [
            {
              query: GetProjectDailyReports,
              variables: {
                after: null,
                customerID: customer_id,
                projectID,
                first: 12,
                orderBy: {
                  direction: OrderDirection.ASC,
                  field: AtsProjectDailyReportsOrderField.REPORT_DATE,
                },
              },
            },
          ],
        }).then(({ data }) => {
          if (data && data.UpdateProjectPmuRemark) {
            setMessage({
              flag: true,
              message: "Remarks updated successfully",
              operation: Operation.UPDATE,
            });
          }
        });
      }
    }
  };

  const handleClose = () => {
    if (message.operation !== Operation.NONE) {
      setPmuRemarks("");
      setDrawer(false);
    }
    setMessage(emptyMessage);
  };

  useEffect(() => {
    if (operation === Operation.UPDATE && AtsProjectDailyReportsID) {
      atsProjectDailyReportsById({
        variables: {
          id: AtsProjectDailyReportsID,
        },
      }).then(({ data }) => {
        if (data && data.node) {
          setPmuRemarks(data.node.pmuRemarks || "");
          setDate(data.node.reportDate);
        }
      });
    }
  }, [operation, AtsProjectDailyReportsID]);

  return (
    <div className="fill-progress-details">
      <div className="fill-progress-details__title">
        <Title>
          {operation === Operation.CREATE ? "Fill" : "Update"} Work Progress
        </Title>
        <img src={Close} alt="" onClick={() => setDrawer(false)} />
      </div>
      <div className="fill-progress-details__block">
        {operation === Operation.CREATE && (
          <div className="fill-progress-details__block--grid">
            <Label>Date</Label>
            <TextField
              type="date"
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
              onChange={(e) => {
                setDate(e.target.value);
              }}
              className="work-progress__row--text-field"
              value={toInputStandardDate(date)}
            />
          </div>
        )}

        <div className="fill-progress-details__block--grid">
          <Label>Pmu Remarks</Label>
          <TextArea
            rows={3}
            autoFocus
            value={pmuRemarks}
            onChange={(e) => setPmuRemarks(e.target.value)}
          />
        </div>
      </div>
      <Button mode="save" loading={addingRemarks} onClick={handleAddRemarks} />
      <Button
        mode="clear"
        loading={addingRemarks}
        onClick={() => setPmuRemarks("")}
      />
      <Button
        mode="cancel"
        loading={addingRemarks}
        onClick={() => setDrawer(false)}
      />
      <MessageModal
        handleClose={handleClose}
        modalFlag={message.flag}
        operation={message.operation}
        value={message.message}
      />
      <LoadingModal flag={addingRemarks || updatingRemarks} />
    </div>
  );
};

export default FillRemarks;
